// src/pages/Admin/Disciplines/CreateDisciplineModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaListOl,
  FaCloudUploadAlt,
  FaLink,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { addDiscipline } from "../../../services/disciplinesServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { uploadDisciplinePhoto } from "../../../services/disciplinePhotoServices";

// URL absolue : http(s) / data: / blob: → ok ; /uploads → préfixe API ; /img/... → tel quel
const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s; // assets front (ex: /img/no-image.jpg)
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${base}${s}`;
};

// Quill: toolbar & formats
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

// Extraction d'ID robuste (AxiosResponse ou data directe)
function extractId(obj) {
  if (!obj) return null;
  const r = obj.data ?? obj;
  return (
    r?.id ??
    r?.insertId ??
    r?.discipline?.id ??
    r?.data?.id ??
    r?.data?.insertId ??
    r?.payload?.id ??
    (Array.isArray(r) ? r[0]?.id : null) ??
    null
  );
}

const CreateDisciplineModal = ({ isActive, onClose, refreshDisciplines }) => {
  const [formData, setFormData] = useState({
    nom_discipline: "",
    description_discipline: "",
    image_discipline: "",
    contenu_discipline: "",
    ordre_discipline: "",
  });

  // Image / aperçu
  const defaultDiscipline = "/img/no-image.jpg";
  const [previewSrc, setPreviewSrc] = useState(defaultDiscipline);

  // Upload local
  const [uploadMode, setUploadMode] = useState("url"); // 'url' | 'upload'
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    // Focus + lock scroll + ESC
    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom_discipline']")?.focus();
    }, 0);

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    // Prévisualisation initiale (si pas de fichier)
    if (!file) {
      const v = formData.image_discipline?.trim();
      setPreviewSrc(v ? toAbsolute(v) : defaultDiscipline);
    }

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onEsc);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Object URL pour le fichier local
  useEffect(() => {
    if (!file) {
      setFilePreview("");
      const v = formData.image_discipline?.trim();
      setPreviewSrc(v ? toAbsolute(v) : defaultDiscipline);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    setPreviewSrc(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const resetForm = () => {
    setFormData({
      nom_discipline: "",
      description_discipline: "",
      image_discipline: "",
      contenu_discipline: "",
      ordre_discipline: "",
    });
    setPreviewSrc(defaultDiscipline);
    setUploadMode("url");
    setFile(null);
    setFilePreview("");
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleClose = () => {
    onClose?.();
    resetForm();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "image_discipline" && !file) {
      const v = value.trim();
      setPreviewSrc(v ? toAbsolute(v) : defaultDiscipline);
    }
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, contenu_discipline: value }));
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    const okType = /^image\/(png|jpe?g|webp)$/i.test(f.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const buildCreatePayload = () => {
    const base = {
      nom_discipline: formData.nom_discipline.trim(),
      description_discipline: formData.description_discipline.trim(),
      contenu_discipline: formData.contenu_discipline || "",
      ordre_discipline:
        String(formData.ordre_discipline || "").trim() === ""
          ? 0
          : Number(formData.ordre_discipline),
    };

    // Mode upload : on laisse l'image vide ici, elle sera renseignée par le PATCH upload
    if (uploadMode === "upload" && file) return base;

    // Mode URL : inclure seulement si non vide
    const img = (formData.image_discipline || "").trim();
    return img ? { ...base, image_discipline: img } : base;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUploading) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const payload = buildCreatePayload();
      if (!payload.nom_discipline) throw new Error("Le nom est requis.");
      if (!payload.description_discipline)
        throw new Error("La description est requise.");
      if (!Number.isFinite(payload.ordre_discipline))
        throw new Error("L'ordre d'affichage doit être un nombre.");

      // 1) Création
      const created = await addDiscipline(payload);
      const newId = extractId(created);

      // 2) Upload image si demandé ET si on a un ID
      if (uploadMode === "upload" && file) {
        if (newId) {
          try {
            await uploadDisciplinePhoto(newId, file, {
              format: outputFormat,
              onUploadProgress: (evt) => {
                if (!evt.total) return;
                const p = Math.round((evt.loaded / evt.total) * 100);
                setUploadProgress(p);
              },
            });
            toast.success(
              "Image de la discipline téléversée et optimisée (400×400)."
            );
          } catch (err) {
            console.error("Upload image discipline échoué:", err);
            toast.error("Discipline créée, mais l'upload de l'image a échoué.");
          }
        } else {
          console.warn(
            "[CreateDiscipline] Discipline créée sans ID exploitable. Upload image ignoré."
          );
          toast.warning(
            "Discipline créée, mais impossible d'associer l'image (ID non retourné)."
          );
        }
      }

      toast.success("Discipline ajoutée avec succès");
      refreshDisciplines?.();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout de la discipline", error);
      toast.error(
        error?.response?.data ||
          error?.message ||
          "Erreur lors de l'ajout de la discipline"
      );
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isActive) return null;

  const displaySrc = toAbsolute(previewSrc || defaultDiscipline);

  // --- PORTAL ---
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter une discipline"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header sticky en dégradé */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Ajouter une Nouvelle Discipline
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Nom, description, image (URL ou upload), contenu enrichi et ordre
              d’affichage.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps + Footer dans le même <form> */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 sm:gap-6">
            {/* Colonne gauche: aperçu + URL/Upload */}
            <div className="flex md:flex-col gap-4 md:gap-5">
              {/* Aperçu visuel */}
              <div className="relative self-start">
                <img
                  src={displaySrc}
                  alt="Aperçu de l’illustration"
                  onError={(e) => {
                    e.currentTarget.src = defaultDiscipline;
                  }}
                  className="h-24 w-24 sm:h-28 sm:w-28 rounded-xl object-cover shadow ring-2 ring-white dark:ring-slate-800"
                />
                <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-[12px]" />
                </span>
              </div>

              {/* Choix mode URL/Upload */}
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="url"
                      checked={uploadMode === "url"}
                      onChange={() => setUploadMode("url")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaLink /> Lien URL
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="upload"
                      checked={uploadMode === "upload"}
                      onChange={() => setUploadMode("upload")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaCloudUploadAlt /> Uploader
                    </span>
                  </label>
                </div>

                {uploadMode === "url" ? (
                  <>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      URL de l’image
                    </label>
                    <input
                      type="url"
                      name="image_discipline"
                      value={formData.image_discipline}
                      onChange={handleChange}
                      placeholder="https://…/image.jpg ou /uploads/photos_disciplines/…"
                      inputMode="url"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      PNG/JPG/WebP — si vous uploadez un fichier, il sera
                      optimisé en 400×400.
                    </p>
                  </>
                ) : (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/70 dark:bg-slate-900/30">
                    {/* INPUT FILE CACHÉ */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center transition ring-1 ring-primary/70 hover:ring-primary"
                          onClick={openFilePicker}
                        >
                          Choisir un fichier
                        </button>

                        <div className="flex items-center gap-2">
                          <span className="text-sm">Format</span>
                          <select
                            className="select select-bordered select-sm"
                            value={outputFormat}
                            onChange={(e) => setOutputFormat(e.target.value)}
                            disabled={isUploading}
                          >
                            <option value="webp">WEBP (recommandé)</option>
                            <option value="jpg">JPG</option>
                            <option value="png">PNG</option>
                          </select>
                        </div>
                      </div>

                      {isUploading && file && (
                        <div>
                          <progress
                            className="progress w-full"
                            value={uploadProgress}
                            max="100"
                          />
                          <div className="text-xs mt-1">{uploadProgress}%</div>
                        </div>
                      )}

                      <p className="text-xs opacity-70">
                        L’image sera traitée côté serveur en carré 400×400 et
                        optimisée après création.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Colonne droite: champs principaux */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Nom */}
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom de la discipline <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="nom_discipline"
                  value={formData.nom_discipline}
                  onChange={handleChange}
                  required
                  placeholder="Ex : Judo, Ju-jitsu, Taïso…"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Description */}
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Description <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="description_discipline"
                  value={formData.description_discipline}
                  onChange={handleChange}
                  required
                  placeholder="Courte description"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Contenu riche */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  Contenu (présentation détaillée)
                </label>
                <div
                  className="[&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                             [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                             [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                             [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={formData.contenu_discipline}
                    onChange={handleContentChange}
                    theme="snow"
                    placeholder="Décrivez la discipline, ses objectifs, ses bienfaits…"
                    modules={modules}
                    formats={formats}
                  />
                </div>
              </div>

              {/* Ordre d’affichage */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Ordre d’affichage <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="ordre_discipline"
                    value={formData.ordre_discipline}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2"
                title="Annuler"
              >
                <FaTimes />
                Annuler
              </button>
              <Button
                text={isUploading ? "Enregistrement..." : "Enregistrer"}
                icon={FaSave}
                type="submit"
                className="!rounded-xl gap-2"
                disabled={isUploading}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Styles locaux : placeholders + scrollbar */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>,
    document.body
  );
};

export default CreateDisciplineModal;
